/* ============================================================
   ARTHAUS THEME — Main JavaScript
   ============================================================ */
'use strict';

/* ── Utilities ────────────────────────────────────────────── */
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function debounce(fn, delay) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
}

function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  return window.Shopify && window.Shopify.currency
    ? `${window.Shopify.currency.active} $${amount}`
    : `$${amount}`;
}

/* ── Scroll reveal ────────────────────────────────────────── */
class ScrollReveal {
  constructor() {
    if (!document.body.classList.contains('js-reveal-enabled')) return;
    if (prefersReducedMotion) {
      document.querySelectorAll('.reveal').forEach(el => el.classList.add('revealed'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
  }
}

/* ── Parallax ─────────────────────────────────────────────── */
class ParallaxEffect {
  constructor() {
    this.elements = [...document.querySelectorAll('[data-parallax]')];
    if (!this.elements.length || prefersReducedMotion) return;
    this.ticking = false;
    this.update = this.update.bind(this);
    window.addEventListener('scroll', () => {
      if (!this.ticking) {
        requestAnimationFrame(this.update);
        this.ticking = true;
      }
    }, { passive: true });
    this.update();
  }

  update() {
    this.ticking = false;
    this.elements.forEach(el => {
      const container = el.closest('[data-parallax-container]');
      if (!container) return;
      const rect = container.getBoundingClientRect();
      if (rect.bottom < -100 || rect.top > window.innerHeight + 100) return;
      const speed = parseFloat(el.dataset.parallaxSpeed) || 0.3;
      const center = rect.top + rect.height / 2 - window.innerHeight / 2;
      el.style.transform = `translate3d(0, ${center * speed}px, 0)`;
    });
  }
}

/* ── Header ───────────────────────────────────────────────── */
class Header {
  constructor() {
    this.header = document.querySelector('.site-header');
    if (!this.header) return;

    this.burger = document.querySelector('.header-burger');
    this.mobileNav = document.querySelector('.mobile-nav');
    this.lastScroll = 0;

    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.burger && this.mobileNav) {
      this.burger.addEventListener('click', () => this.toggleMobileNav());

      this.mobileNav.querySelectorAll('a').forEach(a => {
        a.addEventListener('click', () => this.closeMobileNav());
      });

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape') this.closeMobileNav();
      });
    }

    this.onScroll();
  }

  onScroll() {
    const y = window.scrollY;
    this.header.classList.toggle('is-scrolled', y > 50);
    if (this.header.classList.contains('header--transparent-start')) {
      const solid = y > 80;
      this.header.style.background = solid ? 'var(--color-header-bg)' : 'transparent';
      this.header.style.color = solid ? 'var(--color-header-text)' : '#fff';
      this.header.style.borderBottomColor = solid
        ? 'color-mix(in srgb, var(--color-fg) 10%, transparent)'
        : 'transparent';
    }
    this.lastScroll = y;
  }

  toggleMobileNav() {
    const open = this.burger.classList.toggle('is-open');
    this.mobileNav.classList.toggle('is-open', open);
    document.body.classList.toggle('no-scroll', open);
    this.burger.setAttribute('aria-expanded', open);
  }

  closeMobileNav() {
    this.burger.classList.remove('is-open');
    this.mobileNav.classList.remove('is-open');
    document.body.classList.remove('no-scroll');
    this.burger.setAttribute('aria-expanded', 'false');
  }
}

/* ── Cart Drawer ──────────────────────────────────────────── */
class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-overlay');
    if (!this.drawer) return;
    this.init();
  }

  init() {
    document.addEventListener('click', e => {
      if (e.target.closest('[data-open-cart]')) this.open();
      if (e.target.closest('[data-close-cart]') || e.target === this.overlay) this.close();
    });

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) this.close();
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-open');
    document.body.classList.add('no-scroll');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.refresh();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    document.body.classList.remove('no-scroll');
    this.drawer.setAttribute('aria-hidden', 'true');
  }

  async refresh() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.render(cart);
    } catch (e) { console.error('Cart fetch error', e); }
  }

  render(cart) {
    const itemsEl = this.drawer.querySelector('[data-cart-items]');
    const totalEl = this.drawer.querySelector('[data-cart-total]');
    const countEls = document.querySelectorAll('[data-cart-count]');

    countEls.forEach(el => {
      el.textContent = cart.item_count;
      el.dataset.count = cart.item_count;
    });

    if (totalEl) totalEl.textContent = formatMoney(cart.total_price);

    if (!itemsEl) return;

    if (!cart.item_count) {
      itemsEl.innerHTML = `<p class="cart-drawer__empty">Your cart is empty.</p>`;
      return;
    }

    itemsEl.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <a href="${item.url}">
          <img class="cart-item__image" src="${item.featured_image?.url || item.image}" alt="${item.product_title}" width="80" height="100" loading="lazy">
        </a>
        <div class="cart-item__details">
          <div class="cart-item__title">${item.product_title}</div>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
          <div class="cart-item__price">${formatMoney(item.final_price)}</div>
          <div class="cart-item__qty">
            <button class="qty-btn" data-key="${item.key}" data-action="decrease" aria-label="Decrease quantity">−</button>
            <span class="qty-value">${item.quantity}</span>
            <button class="qty-btn" data-key="${item.key}" data-action="increase" aria-label="Increase quantity">+</button>
          </div>
          <button class="cart-item__remove" data-key="${item.key}">Remove</button>
        </div>
      </div>
    `).join('');

    itemsEl.querySelectorAll('.qty-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const action = btn.dataset.action;
        const item = cart.items.find(i => i.key === key);
        if (!item) return;
        const newQty = action === 'increase' ? item.quantity + 1 : item.quantity - 1;
        await this.updateItem(key, Math.max(0, newQty));
      });
    });

    itemsEl.querySelectorAll('.cart-item__remove').forEach(btn => {
      btn.addEventListener('click', async () => {
        await this.updateItem(btn.dataset.key, 0);
      });
    });
  }

  async updateItem(key, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      this.refresh();
    } catch (e) { console.error('Cart update error', e); }
  }
}

/* ── Add To Cart ──────────────────────────────────────────── */
class AddToCart {
  constructor() {
    document.addEventListener('submit', async e => {
      if (!e.target.closest('[data-atc-form]')) return;
      const form = e.target;
      e.preventDefault();

      const btn = form.querySelector('[type="submit"]');
      const originalText = btn.textContent.trim();
      btn.classList.add('loading');
      btn.disabled = true;
      btn.textContent = 'Adding...';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(form)
        });
        const data = await res.json();

        if (!res.ok) throw new Error(data.description || 'Could not add to cart');

        btn.textContent = 'Added!';
        btn.classList.remove('loading');

        const cartType = document.body.dataset.cartType;
        if (cartType === 'drawer') {
          const drawer = new CartDrawer();
          drawer.open();
        }

        setTimeout(() => {
          btn.disabled = false;
          btn.textContent = originalText;
        }, 2500);
      } catch (err) {
        btn.classList.remove('loading');
        btn.disabled = false;
        btn.textContent = 'Error — try again';
        setTimeout(() => { btn.textContent = originalText; }, 3000);
        console.error('ATC error:', err);
      }
    });
  }
}

/* ── Product gallery ──────────────────────────────────────── */
class ProductGallery {
  constructor() {
    const gallery = document.querySelector('.product-gallery');
    if (!gallery) return;

    this.main = gallery.querySelector('.product-gallery__main img');
    this.thumbs = gallery.querySelectorAll('.product-gallery__thumb');

    this.thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.src;
        if (this.main && src) {
          this.main.src = src;
          this.main.srcset = '';
        }
        this.thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });
  }
}

/* ── Variant selector ─────────────────────────────────────── */
class VariantSelector {
  constructor() {
    const atcWrapper = document.querySelector('[data-atc-form]');
    if (!atcWrapper) return;
    const form = atcWrapper.querySelector('form');
    if (!form) return;

    this.form = form;
    this.productData = null;

    const dataEl = document.getElementById('product-json');
    if (dataEl) {
      try { this.productData = JSON.parse(dataEl.textContent); } catch(e) {}
    }

    form.querySelectorAll('[data-option-index]').forEach(selector => {
      selector.addEventListener('change', () => this.updateVariant());
    });

    form.querySelectorAll('.swatch').forEach(swatch => {
      swatch.addEventListener('click', () => {
        const group = swatch.closest('.option-swatches');
        group.querySelectorAll('.swatch').forEach(s => s.classList.remove('active'));
        swatch.classList.add('active');
        const hiddenInput = group.querySelector('input[type="hidden"]');
        if (hiddenInput) hiddenInput.value = swatch.dataset.value;
        this.updateVariant();
      });
    });

    this.updateVariant();
  }

  getSelectedOptions() {
    const options = [];
    this.form.querySelectorAll('[data-option-index]').forEach(el => {
      options[parseInt(el.dataset.optionIndex)] = el.value;
    });
    this.form.querySelectorAll('.option-swatches').forEach(group => {
      const active = group.querySelector('.swatch.active');
      const idx = parseInt(group.dataset.optionIndex);
      if (active) options[idx] = active.dataset.value;
    });
    return options;
  }

  updateVariant() {
    if (!this.productData) return;
    const selected = this.getSelectedOptions();
    const variant = this.productData.variants.find(v =>
      v.options.every((opt, i) => opt === selected[i])
    );

    if (!variant) return;

    const input = this.form.querySelector('input[name="id"]');
    if (input) input.value = variant.id;

    const priceEl = document.querySelector('.product-info__price');
    if (priceEl) {
      priceEl.innerHTML = variant.compare_at_price > variant.price
        ? `<span class="price--sale">${formatMoney(variant.price)}</span><span class="price--compare">${formatMoney(variant.compare_at_price)}</span>`
        : `<span class="price--regular">${formatMoney(variant.price)}</span>`;
    }

    const btn = this.form.querySelector('[type="submit"]');
    if (btn) {
      if (!variant.available) {
        btn.disabled = true;
        btn.textContent = 'Sold out';
      } else {
        btn.disabled = false;
        btn.textContent = btn.dataset.addText || 'Add to cart';
      }
    }
  }
}

/* ── Masonry Lightbox ─────────────────────────────────────── */
class GalleryLightbox {
  constructor() {
    this.lightbox = document.querySelector('.lightbox');
    if (!this.lightbox) return;

    this.img = this.lightbox.querySelector('.lightbox__img');
    this.images = [];
    this.current = 0;

    document.querySelectorAll('[data-lightbox]').forEach((el, i) => {
      this.images.push(el.dataset.lightboxSrc || el.src || el.href);
      el.addEventListener('click', e => {
        e.preventDefault();
        this.current = i;
        this.open(this.images[i]);
      });
    });

    this.lightbox.querySelector('.lightbox__close')?.addEventListener('click', () => this.close());
    this.lightbox.querySelector('.lightbox__prev')?.addEventListener('click', () => this.prev());
    this.lightbox.querySelector('.lightbox__next')?.addEventListener('click', () => this.next());
    this.lightbox.addEventListener('click', e => { if (e.target === this.lightbox) this.close(); });

    document.addEventListener('keydown', e => {
      if (!this.lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') this.close();
      if (e.key === 'ArrowLeft') this.prev();
      if (e.key === 'ArrowRight') this.next();
    });
  }

  open(src) {
    this.img.src = src;
    this.lightbox.classList.add('is-open');
    document.body.classList.add('no-scroll');
  }
  close() {
    this.lightbox.classList.remove('is-open');
    document.body.classList.remove('no-scroll');
  }
  prev() {
    this.current = (this.current - 1 + this.images.length) % this.images.length;
    this.img.src = this.images[this.current];
  }
  next() {
    this.current = (this.current + 1) % this.images.length;
    this.img.src = this.images[this.current];
  }
}

/* ── Quantity selectors (cart page) ───────────────────────── */
class CartPage {
  constructor() {
    const form = document.querySelector('[data-cart-form]');
    if (!form) return;

    form.querySelectorAll('.qty-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.key;
        const action = btn.dataset.action;
        const valEl = btn.closest('.cart-item__qty').querySelector('.qty-value');
        let qty = parseInt(valEl.textContent) || 1;
        qty = action === 'increase' ? qty + 1 : Math.max(0, qty - 1);
        valEl.textContent = qty;
        const hiddenInput = form.querySelector(`input[name="updates[${key}]"]`);
        if (hiddenInput) hiddenInput.value = qty;
      });
    });
  }
}

/* ── Newsletter form ──────────────────────────────────────── */
class Newsletter {
  constructor() {
    document.querySelectorAll('[data-newsletter-form]').forEach(wrapper => {
      const form = wrapper.querySelector('form');
      if (!form) return;
      form.addEventListener('submit', async e => {
        e.preventDefault();
        const email = form.querySelector('input[type="email"]').value;
        const msg = form.querySelector('.newsletter-message');
        const btn = form.querySelector('button[type="submit"]');

        btn.disabled = true;
        try {
          const res = await fetch(form.action, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams(new FormData(form))
          });
          if (msg) {
            msg.textContent = 'Thanks for subscribing!';
            msg.className = 'newsletter-message newsletter-message--success';
          }
          form.reset();
        } catch (err) {
          if (msg) {
            msg.textContent = 'Something went wrong. Please try again.';
            msg.className = 'newsletter-message newsletter-message--error';
          }
        } finally {
          btn.disabled = false;
        }
      });
    });
  }
}

/* ── Announcement bar dismiss ─────────────────────────────── */
class AnnouncementBar {
  constructor() {
    const bar = document.querySelector('.announcement-bar');
    const dismissBtn = document.querySelector('[data-dismiss-announcement]');
    if (!bar || !dismissBtn) return;

    const barId = bar.dataset.id;
    if (barId && sessionStorage.getItem(`announcement-${barId}`) === 'dismissed') {
      bar.style.display = 'none';
      return;
    }

    dismissBtn.addEventListener('click', () => {
      bar.style.display = 'none';
      if (barId) sessionStorage.setItem(`announcement-${barId}`, 'dismissed');
    });
  }
}

/* ── Smooth anchor links ──────────────────────────────────── */
function initSmoothAnchors() {
  document.querySelectorAll('a[href^="#"]').forEach(a => {
    a.addEventListener('click', e => {
      const target = document.querySelector(a.getAttribute('href'));
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    });
  });
}

/* ── Init ─────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  new ScrollReveal();
  new ParallaxEffect();
  new Header();
  new CartDrawer();
  new AddToCart();
  new ProductGallery();
  new VariantSelector();
  new GalleryLightbox();
  new CartPage();
  new Newsletter();
  new AnnouncementBar();
  initSmoothAnchors();

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');
});
